import { z } from "zod";
import {
  API_KEY_SCOPES,
  DEFAULT_API_KEY_DAYS,
  MAX_API_KEY_DAYS,
  WEBHOOK_EVENTS,
  checkWebhookUrl,
  type ApiKeyScope,
  type ApiKeyStatus,
  type WebhookEvent,
  type WebhookUrlCheck,
} from "@cx/domain";

// ===== API pública: chaves, webhooks e MCP (T3.8, RF-PRF-11) =====

const uniqueList = <T extends string>(list: T[]) => new Set(list).size === list.length;

export const apiKeyCreateInput = z.object({
  name: z
    .string("Informe o nome da chave")
    .trim()
    .min(1, "Informe o nome da chave")
    .max(80, "No máximo 80 caracteres"),
  scopes: z
    .array(z.enum(API_KEY_SCOPES, "Escopo inválido"), "Escolha os escopos")
    .min(1, "Escolha ao menos um escopo")
    .refine(uniqueList, "Escopo repetido"),
  expiresInDays: z
    .number("Informe a validade")
    .int("Use dias inteiros")
    .min(1, "No mínimo 1 dia")
    .max(MAX_API_KEY_DAYS, `No máximo ${MAX_API_KEY_DAYS} dias`)
    .default(DEFAULT_API_KEY_DAYS),
});
export type ApiKeyCreateInput = z.output<typeof apiKeyCreateInput>;

export type ApiKeyView = {
  id: string;
  name: string;
  prefix: string;
  scopes: ApiKeyScope[];
  status: ApiKeyStatus;
  createdBy: { id: string; name: string };
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};
/** O token só aparece aqui (uma vez). */
export type ApiKeyCreated = { key: ApiKeyView; token: string };

const URL_MESSAGES: Record<Exclude<WebhookUrlCheck, { ok: true }>["reason"], string> = {
  invalid_url: "Informe uma URL válida",
  too_long: "No máximo 2.000 caracteres",
  https_required: "Use https://",
  port_not_allowed: "Use a porta padrão do https (443)",
  credentials_not_allowed: "Não coloque usuário e senha na URL",
  fragment_not_allowed: "Tire o # da URL",
  host_not_allowed: "Endereço interno não é aceito",
  ip_not_allowed: "IP privado ou reservado não é aceito",
};

export const webhookUrl = z
  .string("Informe a URL")
  .trim()
  .min(1, "Informe a URL")
  .superRefine((v, ctx) => {
    const r = checkWebhookUrl(v);
    if (!r.ok) ctx.addIssue({ code: "custom", message: URL_MESSAGES[r.reason] });
  });

export const webhookInput = z.object({
  url: webhookUrl,
  description: z
    .string()
    .trim()
    .max(200, "No máximo 200 caracteres")
    .nullish()
    .transform((v) => v || null),
  events: z
    .array(z.enum(WEBHOOK_EVENTS, "Evento inválido"), "Escolha os eventos")
    .min(1, "Escolha ao menos um evento")
    .refine(uniqueList, "Evento repetido"),
});
export type WebhookInput = z.output<typeof webhookInput>;

export type WebhookStatus = "active" | "disabled";
export type WebhookView = {
  id: string;
  url: string;
  description: string | null;
  events: WebhookEvent[];
  status: WebhookStatus;
  /** `manual` | `failures` | `gone` (o destino respondeu 410). */
  disabledReason: string | null;
  disabledAt: string | null;
  failureStreak: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};
/** O segredo só aparece na criação e ao girar. */
export type WebhookCreated = { webhook: WebhookView; secret: string };

export const WEBHOOK_DELIVERY_STATUSES = ["pending", "delivering", "delivered", "failed", "skipped"] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number];
export const webhookDeliveryListQuery = z.object({
  status: z.enum(WEBHOOK_DELIVERY_STATUSES).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type WebhookDeliveryListQuery = z.output<typeof webhookDeliveryListQuery>;

export type WebhookDeliveryView = {
  id: string;
  webhookId: string;
  eventId: string;
  event: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  statusCode: number | null;
  error: string | null;
  responseSnippet: string | null;
  durationMs: number | null;
  nextAttemptAt: string | null;
  lastAttemptAt: string | null;
  deliveredAt: string | null;
  redeliveryOf: string | null;
  createdAt: string;
  /** Só no detalhe: o envelope enviado. */
  payload?: unknown;
};

// ---------- MCP (JSON-RPC 2.0, Streamable HTTP sem estado) ----------

export const MCP_PROTOCOL_VERSIONS = ["2025-06-18", "2025-03-26"] as const;
export const jsonRpcId = z.union([z.string().max(200), z.number().int()]);
export const jsonRpcMessage = z.object({
  jsonrpc: z.literal("2.0"),
  id: jsonRpcId.nullish(),
  method: z.string().min(1).max(100),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type JsonRpcMessage = z.output<typeof jsonRpcMessage>;
export const mcpToolCallParams = z.object({
  name: z.string().min(1).max(100),
  arguments: z.record(z.string(), z.unknown()).optional(),
});

/** Proposta do MCP (agente `mcp`) na tela de propostas. */
export type McpProposalView = {
  id: string;
  kind: "sales_order";
  status: "pending" | "accepted" | "rejected" | "expired";
  summary: string;
  rationale: string | null;
  proposal: unknown;
  apiKeyId: string | null;
  createdBy: string | null;
  createdAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  entityId: string | null;
};
export const mcpProposalListQuery = z.object({
  status: z.enum(["pending", "accepted", "rejected", "expired"]).default("pending"),
});
export const MCP_PROPOSAL_REASON_MAX = 500;
